// Program creation wizard (/programs/new) — admin only.
//
// One wizard, two ways to start: type it in, or upload an insertion-order PDF and
// have the fields prefilled from it. Both paths walk the same steps and end in the
// same single POST /programs/, so an imported program is indistinguishable from a
// hand-built one — same validation, same notifications, same defaults. The PDF path
// only changes where the initial values came from.
//
// Nothing is written until "Create Program" on the last step. Components and goals
// ride along in that one request, so an abandoned wizard leaves nothing behind but
// the pending S3 object, which Cancel discards and a lifecycle rule expires anyway.
//
// Targeting is deliberately NOT set up here — extracted targeting rides along as a
// reference line on each component's notes (backend `_component_notes_with_targeting`),
// and real targeting config happens in QA Rules. An earlier version of the import
// minted a list per targeting type per component and left production holding dozens
// of near-duplicates.
import { useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import WizardChrome from '../components/WizardChrome';
import WizardStepper from '../components/WizardStepper';
import SearchableSelect from '../components/SearchableSelect';
import MultiSelect from '../components/MultiSelect';
import FormField, { FORM_INPUT_STYLE } from '../components/FormField';
import FileDropZone, { isPdfFile } from '../components/FileDropZone';
import ClientFormFields, { emptyClientForm } from '../components/ClientFormFields';

export { isPdfFile };

const API = process.env.REACT_APP_API_URL;

// Core NeTrA's own look, not ContentIQ's "Gallery" palette. WizardChrome and
// WizardStepper default to the ContentIQ theme; passing this keeps the Programs
// wizard reading as part of the app it actually lives in.
const NETRA_THEME = {
  accent: '#2c3e50',
  link: '#2c3e50',
  connector: '#2c3e50',
  display: 'inherit',
  body: 'inherit',
  // Seven steps do not fit at the shared component's default 168px/56px.
  stepWidth: '104px',
  connectorWidth: '26px',
  shell: { background: '#ffffff', minHeight: '70vh' },
};

const STEPS = [
  { key: 'start', label: 'Start' },
  { key: 'client', label: 'Client' },
  { key: 'program', label: 'Program' },
  { key: 'document', label: 'Insertion Order' },
  { key: 'components', label: 'Components' },
  { key: 'goals', label: 'Goals' },
  { key: 'review', label: 'Review' },
];

const emptyProgram = () => ({
  po_number: '', program_name: '', client_id: '', agency_id: '', status: '',
  program_manager_id: '', supporting_manager_ids: [],
  order_date: '', flight_date_start: '', flight_date_end: '',
  total_revenue: '', leads_ordered: '', payment_terms: '', notes: '',
});

const emptyComponent = (program) => ({
  component_name: '', product_id: '', product_name: '',
  quantity_ordered: '', cost_per: '',
  flight_date_start: program?.flight_date_start || '',
  flight_date_end: program?.flight_date_end || '',
  notes: '', targeting: null, goals: [],
});

const emptyGoal = (component) => ({
  goal_name: '', supplier_id: '', quantity_ordered: '', spend_per: '',
  flight_date_start: component?.flight_date_start || '',
  flight_date_end: component?.flight_date_end || '',
  notes: '',
});

const card = {
  border: '1px solid #e6e6e6', borderRadius: '8px', padding: '16px',
  marginBottom: '14px', background: '#fbfbfb',
};
const sectionTitle = { margin: '0 0 14px', fontSize: '16px', color: '#2c3e50' };
const grid2 = { display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '14px' };
const grid3 = { display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: '14px' };
const smallButton = {
  padding: '6px 12px', background: '#fff', color: '#2c3e50',
  border: '1px solid #ccd1d6', borderRadius: '4px', cursor: 'pointer', fontSize: '13px',
};
const dangerLink = {
  background: 'none', border: 'none', color: '#c0392b',
  cursor: 'pointer', fontSize: '12px', padding: 0,
};
const bodyPad = { padding: '4px 40px 24px', maxWidth: '1000px', width: '100%', margin: '0 auto' };

const toNum = (v, parse) => (v === '' || v === null || v === undefined ? null : parse(v));
const toInt = (v) => toNum(v, (x) => parseInt(x, 10));
const toFloat = (v) => toNum(v, parseFloat);

// Blank flight dates are common on a freshly extracted IO, so this can't assume
// both are present — it only fires when there's a genuine ordering error.
export function dateRangeError(start, end) {
  if (!start || !end) return null;
  return end < start ? 'Flight End is before Flight Start' : null;
}

// The program-level fields the extraction prefills, as form-state strings.
// Pure and exported because "it's on the IO but never shows up in the wizard"
// is exactly the bug this mapping had for total_revenue/leads_ordered.
export function extractedProgramFields(data) {
  const str = (v) => (v === null || v === undefined ? '' : String(v));
  return {
    po_number: data.po_number || '',
    program_name: data.program_name || '',
    payment_terms: data.payment_terms || '',
    client_id: data.client_id ? String(data.client_id) : '',
    agency_id: data.agency_id ? String(data.agency_id) : '',
    order_date: data.order_date || '',
    flight_date_start: data.flight_date_start || '',
    flight_date_end: data.flight_date_end || '',
    total_revenue: str(data.total_revenue),
    leads_ordered: str(data.leads_ordered),
  };
}

// The whole program tree as one request body. Exported (and pure) because the
// coercion is where this can silently go wrong: form state is all strings, and a
// blank has to reach the API as null rather than 0 or "".
export function buildCreatePayload(program, components, pdf) {
  return {
    po_number: program.po_number || null,
    program_name: program.program_name || null,
    client_id: toInt(program.client_id),
    agency_id: toInt(program.agency_id),
    status: program.status || undefined,
    program_manager_id: toInt(program.program_manager_id),
    supporting_manager_ids: (program.supporting_manager_ids || []).map(v => parseInt(v, 10)),
    order_date: program.order_date || null,
    flight_date_start: program.flight_date_start || null,
    flight_date_end: program.flight_date_end || null,
    total_revenue: toFloat(program.total_revenue),
    leads_ordered: toInt(program.leads_ordered),
    payment_terms: program.payment_terms || null,
    notes: program.notes || null,
    pdf_key: pdf.key || null,
    pdf_filename: pdf.filename || null,
    components: components.map(c => ({
      component_name: c.component_name || null,
      product_id: toInt(c.product_id),
      // Only ever one of the two: a picked product wins, and sending the leftover
      // extracted name alongside it would risk creating a duplicate product row.
      product_name: c.product_id ? null : (c.product_name || null),
      quantity_ordered: toInt(c.quantity_ordered),
      cost_per: toFloat(c.cost_per),
      flight_date_start: c.flight_date_start || null,
      flight_date_end: c.flight_date_end || null,
      notes: c.notes || null,
      targeting: c.targeting || null,
      goals: (c.goals || []).map(g => ({
        goal_name: g.goal_name || null,
        supplier_id: toInt(g.supplier_id),
        quantity_ordered: toInt(g.quantity_ordered),
        spend_per: toFloat(g.spend_per),
        flight_date_start: g.flight_date_start || null,
        flight_date_end: g.flight_date_end || null,
        notes: g.notes || null,
      })),
    })),
  };
}

export default function ProgramWizard() {
  const navigate = useNavigate();
  const fileRef = useRef(null);

  const [stepKey, setStepKey] = useState('start');
  const [path, setPath] = useState(null);            // 'manual' | 'pdf'
  const [program, setProgram] = useState(emptyProgram());
  const [components, setComponents] = useState([]);
  // `file` is the File object itself, kept so the Review step can open the exact
  // document the admin is about to attach — it only lives in S3 under a pending
  // key until Create, and there is no read endpoint for pending objects.
  const [pdf, setPdf] = useState({ key: null, filename: null, skipped: false, file: null });
  // Names the IO mentioned that don't match an existing client/agency row — the
  // Client step offers to create them rather than the backend minting one silently.
  const [unmatched, setUnmatched] = useState({ client: null, agency: null });
  const [busy, setBusy] = useState('');              // '' | 'uploading' | 'creating'
  const [error, setError] = useState(null);
  const [errors, setErrors] = useState({});

  const [clients, setClients] = useState([]);
  const [products, setProducts] = useState([]);
  const [suppliers, setSuppliers] = useState([]);
  const [managers, setManagers] = useState([]);
  const [statuses, setStatuses] = useState([]);

  // Quick-add client, reused for both the Client and Agency pickers — `quickAdd`
  // holds which field the newly created row should land in.
  const [quickAdd, setQuickAdd] = useState(null);    // null | 'client_id' | 'agency_id'
  const [quickForm, setQuickForm] = useState(emptyClientForm());
  const [quickSaving, setQuickSaving] = useState(false);
  const [quickError, setQuickError] = useState(null);

  useEffect(() => {
    const load = (url, set) => axios.get(`${API}${url}`).then(r => set(r.data)).catch(() => {});
    load('/clients/', setClients);
    load('/products/', setProducts);
    load('/suppliers/', setSuppliers);
    load('/users/?role=admin,program_manager,supporting_program_manager', setManagers);
    load('/lookup-values/?category=program_status', setStatuses);
  }, []);

  const clientOpts = useMemo(
    () => clients.map(c => ({ value: String(c.id), label: c.name })), [clients]);
  const productOpts = useMemo(
    () => products.map(p => ({ value: String(p.id), label: p.name })), [products]);
  const supplierOpts = useMemo(
    () => suppliers.map(s => ({ value: String(s.id), label: s.name })), [suppliers]);
  const managerOpts = useMemo(
    () => managers.map(m => ({ value: String(m.id), label: m.name })), [managers]);
  const supportingOpts = useMemo(
    () => managerOpts.filter(o => o.value !== String(program.program_manager_id)),
    [managerOpts, program.program_manager_id]);

  const stepIndex = STEPS.findIndex(s => s.key === stepKey);
  const setField = (key, value) => {
    setProgram(p => ({ ...p, [key]: value }));
    setErrors(e => ({ ...e, [key]: undefined }));
  };

  // ---- PDF handling -------------------------------------------------------

  // Both wizard paths upload through preview-pdf; `extract` is what differs. On the
  // manual path there's nothing to extract from — the admin is typing the program in
  // — so skipping it saves a Claude call and a slow round trip.
  const uploadPdf = async (file, { extract }) => {
    if (!file) return null;
    if (!isPdfFile(file)) {
      setError(`${file.name || 'That file'} isn't a PDF. The insertion order has to be a PDF.`);
      return null;
    }
    setBusy('uploading');
    setError(null);
    try {
      const fd = new FormData();
      fd.append('file', file);
      const res = await axios.post(`${API}/programs/preview-pdf?extract=${extract}`, fd);
      setPdf({ key: res.data.pdf_key, filename: res.data.pdf_filename, skipped: false, file });
      return res.data;
    } catch (err) {
      setError(err.response?.data?.detail || err.message);
      return null;
    } finally {
      setBusy('');
    }
  };

  // Start step: dropped on, or picked from, the "Import an insertion order" tile.
  const startWithPdf = async (file) => {
    const data = await uploadPdf(file, { extract: true });
    if (!data) return;
    prefillFromExtraction(data);
    setPath('pdf');
    setStepKey('client');
  };

  const handleAttachPdf = async (e) => {
    await uploadPdf(e.target.files?.[0], { extract: false });
    e.target.value = '';
  };

  // Opens the attached PDF in a new tab straight from the in-memory File — the
  // same document the backend holds under the pending key.
  const openPdf = () => {
    if (!pdf.file) return;
    const url = URL.createObjectURL(pdf.file);
    window.open(url, '_blank');
    setTimeout(() => URL.revokeObjectURL(url), 60000);
  };

  const prefillFromExtraction = (data) => {
    setProgram(p => ({ ...p, ...extractedProgramFields(data) }));
    setComponents((data.components || []).map(c => ({
      component_name: c.component_name || '',
      product_id: '',
      product_name: c.product_name || '',
      quantity_ordered: c.quantity_ordered ?? '',
      cost_per: c.cost_per ?? '',
      flight_date_start: c.flight_date_start || data.flight_date_start || '',
      flight_date_end: c.flight_date_end || data.flight_date_end || '',
      notes: c.notes || '',
      // Carried through untouched — the backend folds it into the component's
      // notes. There's no targeting editor here on purpose.
      targeting: c.targeting || null,
      goals: [],
    })));
    // The extraction only ever reports names; a name with no matching client row
    // comes back unresolved and the Client step offers to create it.
    setUnmatched({
      client: !data.client_id && data.advertiser_name ? data.advertiser_name : null,
      agency: !data.agency_id && data.agency_name ? data.agency_name : null,
    });
  };

  const discardPdf = () => {
    if (pdf.key) axios.post(`${API}/programs/discard-import-pdf`, { pdf_key: pdf.key }).catch(() => {});
  };

  const handleCancel = () => {
    discardPdf();
    navigate('/programs');
  };

  // ---- Component / goal editing -------------------------------------------

  const patchComponent = (i, patch) =>
    setComponents(cs => cs.map((c, idx) => (idx === i ? { ...c, ...patch } : c)));
  const removeComponent = (i) => setComponents(cs => cs.filter((_, idx) => idx !== i));
  const addComponent = () => setComponents(cs => [...cs, emptyComponent(program)]);

  const patchGoal = (ci, gi, patch) =>
    setComponents(cs => cs.map((c, idx) => (idx !== ci ? c : {
      ...c, goals: c.goals.map((g, gdx) => (gdx === gi ? { ...g, ...patch } : g)),
    })));
  const addGoal = (ci) =>
    setComponents(cs => cs.map((c, idx) => (idx !== ci ? c : {
      ...c, goals: [...c.goals, emptyGoal(c)],
    })));
  const removeGoal = (ci, gi) =>
    setComponents(cs => cs.map((c, idx) => (idx !== ci ? c : {
      ...c, goals: c.goals.filter((_, gdx) => gdx !== gi),
    })));

  // ---- Step validation ----------------------------------------------------

  const validateStep = () => {
    if (stepKey === 'client') {
      if (!program.client_id) {
        setErrors({ client_id: 'Client is required' });
        return false;
      }
    }
    if (stepKey === 'program') {
      const next = {};
      if (!program.program_name.trim()) next.program_name = 'Program Name is required';
      if (!program.order_date) next.order_date = 'Order Date is required';
      if (!program.flight_date_start) next.flight_date_start = 'Flight Start is required';
      if (!program.flight_date_end) next.flight_date_end = 'Flight End is required';
      const range = dateRangeError(program.flight_date_start, program.flight_date_end);
      if (range) next.flight_date_end = range;
      if (Object.keys(next).length) {
        setErrors(next);
        return false;
      }
    }
    if (stepKey === 'document' && !pdf.key && !pdf.skipped) {
      setError('Attach the insertion order, or explicitly continue without one.');
      return false;
    }
    setError(null);
    setErrors({});
    return true;
  };

  const goNext = () => {
    if (!validateStep()) return;
    // Inherit the program's flight dates into any component that hasn't got its
    // own yet — the same dates re-typed at every level was the original complaint.
    if (stepKey === 'program') {
      setComponents(cs => cs.map(c => ({
        ...c,
        flight_date_start: c.flight_date_start || program.flight_date_start,
        flight_date_end: c.flight_date_end || program.flight_date_end,
      })));
    }
    setStepKey(STEPS[Math.min(stepIndex + 1, STEPS.length - 1)].key);
  };

  const goBack = () => {
    setError(null);
    if (stepIndex <= 0) return handleCancel();
    setStepKey(STEPS[stepIndex - 1].key);
  };

  // ---- Create -------------------------------------------------------------

  const handleCreate = async () => {
    setBusy('creating');
    setError(null);
    try {
      const res = await axios.post(`${API}/programs/`, buildCreatePayload(program, components, pdf));
      const goalCount = components.reduce((n, c) => n + c.goals.length, 0);
      navigate('/programs', {
        state: {
          createdProgram: {
            id: res.data.id,
            name: res.data.program_name || 'Program',
            components: components.length,
            goals: goalCount,
            hasIoPdf: !!res.data.io_pdf_filename,
          },
        },
      });
    } catch (err) {
      const detail = err.response?.data?.detail;
      setError(Array.isArray(detail)
        ? detail.map(d => `${(d.loc || []).slice(1).join('.')}: ${d.msg}`).join('; ')
        : (detail || err.message));
    } finally {
      setBusy('');
    }
  };

  // ---- Quick-add client ---------------------------------------------------

  const submitQuickAdd = async () => {
    if (!quickForm.name.trim()) return;
    setQuickSaving(true);
    setQuickError(null);
    try {
      const res = await axios.post(`${API}/clients/`, {
        name: quickForm.name.trim(),
        sales_person: quickForm.sales_person.trim() || null,
        client_code: quickForm.client_code.trim() || null,
      });
      const list = await axios.get(`${API}/clients/`);
      setClients(list.data);
      setField(quickAdd, String(res.data.id));
      setUnmatched(u => ({ ...u, [quickAdd === 'client_id' ? 'client' : 'agency']: null }));
      setQuickAdd(null);
      setQuickForm(emptyClientForm());
    } catch (err) {
      setQuickError(err.response?.data?.detail || err.message);
    } finally {
      setQuickSaving(false);
    }
  };

  const openQuickAdd = (field, prefill) => {
    setQuickAdd(field);
    setQuickForm({ ...emptyClientForm(), name: prefill || '' });
    setQuickError(null);
  };

  // ---- Render -------------------------------------------------------------

  const stepperSteps = STEPS.map((s, i) => ({
    key: s.key,
    label: s.label,
    state: i < stepIndex ? 'done' : i === stepIndex ? 'current' : 'todo',
    clickable: i < stepIndex,
  }));

  const footerFor = () => {
    if (stepKey === 'start') {
      return { backLabel: 'Cancel', onBack: handleCancel, note: busy === 'uploading' ? 'Reading the PDF…' : null };
    }
    if (stepKey === 'review') {
      return {
        backLabel: '← Back', onBack: goBack,
        continueLabel: busy === 'creating' ? 'Creating…' : 'Create Program',
        onContinue: handleCreate, continueDisabled: busy === 'creating',
      };
    }
    const optional = stepKey === 'components' || stepKey === 'goals';
    return {
      backLabel: '← Back', onBack: goBack,
      note: optional ? 'Optional — you can add these later on the Components and Goals pages' : null,
      continueLabel: 'Continue →', onContinue: goNext,
      continueDisabled: busy === 'uploading',
    };
  };

  return (
    <WizardChrome
      title="New Program"
      backLabel="← Programs"
      onBack={handleCancel}
      theme={NETRA_THEME}
      footer={footerFor()}
    >
      <WizardStepper steps={stepperSteps} theme={NETRA_THEME}
        onStepClick={(k) => setStepKey(k)} />

      <div style={bodyPad}>
        {error && (
          <div style={{
            background: '#fdecea', border: '1px solid #f5c6cb', color: '#a94442',
            borderRadius: '6px', padding: '10px 14px', marginBottom: '16px', fontSize: '13px',
          }}>
            {error}
          </div>
        )}

        {stepKey === 'start' && (
          <StartStep
            busy={busy}
            onManual={() => { setPath('manual'); setStepKey('client'); }}
            onPdfFile={startWithPdf}
          />
        )}

        {stepKey === 'client' && (
          <ClientStep
            program={program} setField={setField} errors={errors}
            clientOpts={clientOpts} unmatched={unmatched} openQuickAdd={openQuickAdd}
          />
        )}

        {stepKey === 'program' && (
          <ProgramStep
            program={program} setField={setField} errors={errors}
            statuses={statuses} managerOpts={managerOpts} supportingOpts={supportingOpts}
          />
        )}

        {stepKey === 'document' && (
          <DocumentStep
            pdf={pdf} path={path} busy={busy}
            onAttach={() => fileRef.current?.click()}
            onOpenPdf={pdf.file ? openPdf : null}
            onSkipChange={(v) => { setPdf(p => ({ ...p, skipped: v })); setError(null); }}
          />
        )}

        {stepKey === 'components' && (
          <ComponentsStep
            components={components} productOpts={productOpts}
            patchComponent={patchComponent} removeComponent={removeComponent}
            addComponent={addComponent}
          />
        )}

        {stepKey === 'goals' && (
          <GoalsStep
            components={components} supplierOpts={supplierOpts}
            patchGoal={patchGoal} addGoal={addGoal} removeGoal={removeGoal}
          />
        )}

        {stepKey === 'review' && (
          <ReviewStep
            program={program} components={components} pdf={pdf} clients={clients}
            onOpenPdf={pdf.file ? openPdf : null}
          />
        )}
      </div>

      <input
        type="file" accept="application/pdf" ref={fileRef} style={{ display: 'none' }}
        onChange={handleAttachPdf}
      />

      {quickAdd && (
        <QuickAddClientModal
          isAgency={quickAdd === 'agency_id'}
          form={quickForm} setForm={setQuickForm}
          saving={quickSaving} error={quickError}
          onSave={submitQuickAdd} onClose={() => setQuickAdd(null)}
        />
      )}
    </WizardChrome>
  );
}

// ---------------------------------------------------------------------------
// Steps
// ---------------------------------------------------------------------------

// The IO tile is a drop target (Edward, 2026-09-08): drag the PDF onto it, or
// click it to browse. Same shared FileDropZone ContentIQ uses, in NeTrA's colours.
const DROP_COLORS = {
  accent: '#2c3e50', border: '#e6e6e6', tint: '#eef2f6', band: '#fff',
  text: '#2c3e50', muted: '#666', radius: '8px',
};

export function StartStep({ busy, onManual, onPdfFile }) {
  const choice = {
    ...card, cursor: 'pointer', textAlign: 'left', width: '100%',
    background: '#fff', padding: '22px',
  };
  return (
    <div>
      <h3 style={sectionTitle}>How do you want to start?</h3>
      <div style={grid2}>
        <button type="button" style={choice} onClick={onManual} disabled={!!busy}>
          <div style={{ fontSize: '15px', fontWeight: 700, color: '#2c3e50', marginBottom: '6px' }}>
            ✍️ Enter it manually
          </div>
          <div style={{ fontSize: '13px', color: '#666', lineHeight: 1.5 }}>
            Fill in the program yourself. You'll still attach the insertion order along the way.
          </div>
        </button>
        <FileDropZone
          accept="application/pdf" multiple={false} disabled={!!busy}
          colors={DROP_COLORS}
          style={{ ...choice, borderStyle: 'dashed', marginBottom: 0, padding: '22px' }}
          onFiles={(files) => onPdfFile(files[0])}
        >
          <div style={{ fontSize: '15px', fontWeight: 700, color: '#2c3e50', marginBottom: '6px' }}>
            📄 {busy === 'uploading' ? 'Reading the PDF…' : 'Import an insertion order'}
          </div>
          <div style={{ fontSize: '13px', color: '#666', lineHeight: 1.5 }}>
            Drop the IO PDF here, or click to browse. Its details are read out and used to
            prefill every step, and the file itself stays attached to the program.
          </div>
        </FileDropZone>
      </div>
    </div>
  );
}

// A link-styled button that opens the attached PDF in a new tab. A real <a>
// would need an href, and the file has no URL yet (pending S3 key, no reader).
function OpenPdfLink({ filename, onOpen }) {
  return (
    <button type="button" onClick={onOpen}
      title="Open the insertion order in a new tab"
      style={{
        background: 'none', border: 'none', padding: 0, cursor: 'pointer',
        color: '#1a6da8', textDecoration: 'underline', fontSize: 'inherit', fontWeight: 'inherit',
      }}>
      {filename || 'insertion-order.pdf'} ↗
    </button>
  );
}

export function ClientStep({ program, setField, errors, clientOpts, unmatched, openQuickAdd }) {
  const notFound = (name, field) => (
    <div style={{
      background: '#fef9e7', border: '1px solid #f7dc6f', borderRadius: '6px',
      padding: '10px 14px', marginTop: '8px', fontSize: '13px', color: '#7d6608',
    }}>
      The insertion order names <strong>{name}</strong>, which isn't in NeTrA yet.
      {' '}
      <button type="button" style={{ ...dangerLink, color: '#7d6608', textDecoration: 'underline' }}
        onClick={() => openQuickAdd(field, name)}>
        Create it
      </button>
    </div>
  );
  return (
    <div>
      <h3 style={sectionTitle}>Who is this program for?</h3>
      <div style={grid2}>
        <div>
          <SearchableSelect
            label="Client *"
            options={clientOpts}
            value={program.client_id}
            onChange={(v) => setField('client_id', v)}
            placeholder="Search clients…"
          />
          <button type="button" style={{ ...smallButton, marginTop: '8px' }}
            onClick={() => openQuickAdd('client_id')}>
            + New Client
          </button>
          {errors.client_id && (
            <div style={{ color: '#c0392b', fontSize: '12px', marginTop: '6px' }}>{errors.client_id}</div>
          )}
          {unmatched.client && notFound(unmatched.client, 'client_id')}
        </div>
        <div>
          <SearchableSelect
            label="Agency"
            options={clientOpts}
            value={program.agency_id}
            onChange={(v) => setField('agency_id', v)}
            placeholder="Search agencies…"
          />
          <button type="button" style={{ ...smallButton, marginTop: '8px' }}
            onClick={() => openQuickAdd('agency_id')}>
            + New Agency
          </button>
          {unmatched.agency && notFound(unmatched.agency, 'agency_id')}
        </div>
      </div>
      <p style={{ fontSize: '12px', color: '#888', marginTop: '14px' }}>
        Agencies are stored alongside clients, so both pickers search the same list.
      </p>
    </div>
  );
}

export function ProgramStep({ program, setField, errors, statuses, managerOpts, supportingOpts }) {
  return (
    <div>
      <h3 style={sectionTitle}>Program details</h3>
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '14px' }}>
        <FormField label="Program Name" required error={errors.program_name}>
          <input style={FORM_INPUT_STYLE} value={program.program_name}
            onChange={e => setField('program_name', e.target.value)} />
        </FormField>
        <FormField label="PO Number">
          <input style={FORM_INPUT_STYLE} value={program.po_number}
            onChange={e => setField('po_number', e.target.value)} />
        </FormField>
        <FormField label="Status">
          <select style={FORM_INPUT_STYLE} value={program.status}
            onChange={e => setField('status', e.target.value)}>
            <option value="">Pending Kickoff (default)</option>
            {statuses.map(s => <option key={s.value} value={s.value}>{s.value}</option>)}
          </select>
        </FormField>
        <FormField label="Program Manager">
          <select style={FORM_INPUT_STYLE} value={program.program_manager_id}
            onChange={e => setField('program_manager_id', e.target.value)}>
            <option value="">—</option>
            {managerOpts.map(m => <option key={m.value} value={m.value}>{m.label}</option>)}
          </select>
        </FormField>
        <div style={{ gridColumn: 'span 2' }}>
          <MultiSelect
            label="Supporting Program Managers"
            options={supportingOpts}
            values={program.supporting_manager_ids}
            onChange={(v) => setField('supporting_manager_ids', v)}
            placeholder="Add supporting PMs…"
          />
        </div>
        <FormField label="Order Date" required error={errors.order_date}>
          <input type="date" style={FORM_INPUT_STYLE} value={program.order_date}
            onChange={e => setField('order_date', e.target.value)} />
        </FormField>
        <div />
        <FormField label="Flight Start" required error={errors.flight_date_start}>
          <input type="date" style={FORM_INPUT_STYLE} value={program.flight_date_start}
            onChange={e => setField('flight_date_start', e.target.value)} />
        </FormField>
        <FormField label="Flight End" required error={errors.flight_date_end}>
          <input type="date" style={FORM_INPUT_STYLE} value={program.flight_date_end}
            onChange={e => setField('flight_date_end', e.target.value)} />
        </FormField>
        <FormField label="Total Revenue">
          <input type="number" step="0.01" style={FORM_INPUT_STYLE} value={program.total_revenue}
            onChange={e => setField('total_revenue', e.target.value)} />
        </FormField>
        <FormField label="Leads Ordered">
          <input type="number" style={FORM_INPUT_STYLE} value={program.leads_ordered}
            onChange={e => setField('leads_ordered', e.target.value)} />
        </FormField>
        <FormField label="Payment Terms">
          <input style={FORM_INPUT_STYLE} value={program.payment_terms}
            onChange={e => setField('payment_terms', e.target.value)} />
        </FormField>
        <div />
        <FormField label="Notes" gridColumn="span 2">
          <textarea rows={3} style={FORM_INPUT_STYLE} value={program.notes}
            onChange={e => setField('notes', e.target.value)} />
        </FormField>
      </div>
    </div>
  );
}

export function DocumentStep({ pdf, path, busy, onAttach, onSkipChange, onOpenPdf }) {
  return (
    <div>
      <h3 style={sectionTitle}>Insertion order</h3>
      {pdf.key ? (
        <div style={{ ...card, background: '#eafaf1', borderColor: '#a9dfbf' }}>
          <div style={{ fontSize: '14px', color: '#1e7e45', fontWeight: 600 }}>
            ✓ {pdf.filename || 'insertion-order.pdf'} attached
            {onOpenPdf && (
              <span style={{ marginLeft: '10px', fontWeight: 400, fontSize: '13px' }}>
                <OpenPdfLink filename="Open" onOpen={onOpenPdf} />
              </span>
            )}
          </div>
          <div style={{ fontSize: '12px', color: '#555', marginTop: '6px' }}>
            {path === 'pdf'
              ? 'This is the PDF the program details were read from. It stays attached to the program.'
              : 'Stored with the program and viewable from the drilldown once it exists.'}
          </div>
          <button type="button" style={{ ...smallButton, marginTop: '10px' }} onClick={onAttach}
            disabled={busy === 'uploading'}>
            {busy === 'uploading' ? 'Uploading…' : 'Replace PDF'}
          </button>
        </div>
      ) : (
        <>
          <p style={{ fontSize: '13px', color: '#555', lineHeight: 1.6 }}>
            Every program should have its insertion order or contract on file. PDFs only, 25MB max.
          </p>
          <button type="button" style={{ ...smallButton, padding: '10px 18px' }} onClick={onAttach}
            disabled={busy === 'uploading'}>
            {busy === 'uploading' ? 'Uploading…' : '📎 Attach the insertion order'}
          </button>
          <label style={{
            display: 'flex', alignItems: 'flex-start', gap: '8px', marginTop: '20px',
            fontSize: '13px', color: '#555', cursor: 'pointer', maxWidth: '620px',
          }}>
            <input type="checkbox" checked={pdf.skipped} style={{ marginTop: '3px' }}
              onChange={e => onSkipChange(e.target.checked)} />
            <span>
              The paperwork isn't signed yet — create this program without it. The program
              will show as having no insertion order on file until someone attaches one.
            </span>
          </label>
        </>
      )}
    </div>
  );
}

export function ComponentsStep({ components, productOpts, patchComponent, removeComponent, addComponent }) {
  return (
    <div>
      <h3 style={sectionTitle}>Components</h3>
      <p style={{ fontSize: '13px', color: '#666', marginTop: '-8px', marginBottom: '16px' }}>
        The line items on the order. Keep it minimal here — everything else about a
        component is editable on the Components page.
      </p>
      {components.map((c, i) => (
        <div key={i} style={card}>
          <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '12px' }}>
            <strong style={{ fontSize: '13px', color: '#2c3e50' }}>
              {c.component_name || `Component ${i + 1}`}
            </strong>
            <button type="button" style={dangerLink} onClick={() => removeComponent(i)}>Remove</button>
          </div>
          <div style={grid2}>
            <FormField label="Component Name">
              <input style={FORM_INPUT_STYLE} value={c.component_name}
                onChange={e => patchComponent(i, { component_name: e.target.value })} />
            </FormField>
            <div>
              <SearchableSelect
                label="Product"
                options={productOpts}
                value={c.product_id}
                onChange={(v) => patchComponent(i, { product_id: v })}
                placeholder="Search products…"
              />
              {/* The extraction reports a product by name. Leaving it unmatched is
                  allowed — the backend resolves it, creating the product only if the
                  name is genuinely new. */}
              {!c.product_id && c.product_name && (
                <div style={{ fontSize: '12px', color: '#7d6608', marginTop: '6px' }}>
                  From the IO: “{c.product_name}” — pick a product to match it to, or leave
                  it and it'll be matched on name.
                </div>
              )}
            </div>
          </div>
          <div style={{ ...grid3, marginTop: '14px' }}>
            <FormField label="Quantity Ordered">
              <input type="number" style={FORM_INPUT_STYLE} value={c.quantity_ordered}
                onChange={e => patchComponent(i, { quantity_ordered: e.target.value })} />
            </FormField>
            <FormField label="Cost Per">
              <input type="number" step="0.01" style={FORM_INPUT_STYLE} value={c.cost_per}
                onChange={e => patchComponent(i, { cost_per: e.target.value })} />
            </FormField>
            <div />
            <FormField label="Flight Start">
              <input type="date" style={FORM_INPUT_STYLE} value={c.flight_date_start}
                onChange={e => patchComponent(i, { flight_date_start: e.target.value })} />
            </FormField>
            <FormField label="Flight End"
              error={dateRangeError(c.flight_date_start, c.flight_date_end)}>
              <input type="date" style={FORM_INPUT_STYLE} value={c.flight_date_end}
                onChange={e => patchComponent(i, { flight_date_end: e.target.value })} />
            </FormField>
          </div>
          <div style={{ marginTop: '14px' }}>
            <FormField label="Notes">
              <textarea rows={2} style={FORM_INPUT_STYLE} value={c.notes}
                onChange={e => patchComponent(i, { notes: e.target.value })} />
            </FormField>
            {c.targeting && (
              <div style={{ fontSize: '12px', color: '#888', marginTop: '6px' }}>
                Targeting read from the IO is appended to these notes for reference.
                Set up real targeting in QA Rules.
              </div>
            )}
          </div>
        </div>
      ))}
      <button type="button" style={smallButton} onClick={addComponent}>+ Add Component</button>
      {components.length === 0 && (
        <p style={{ fontSize: '13px', color: '#888', marginTop: '14px' }}>
          No components yet. You can skip this and add them on the Components page later.
        </p>
      )}
    </div>
  );
}

export function GoalsStep({ components, supplierOpts, patchGoal, addGoal, removeGoal }) {
  if (components.length === 0) {
    return (
      <div>
        <h3 style={sectionTitle}>Goals</h3>
        <p style={{ fontSize: '13px', color: '#666' }}>
          Goals hang off components, and this program has none yet. Add components on the
          previous step, or add both later from the Components and Goals pages.
        </p>
      </div>
    );
  }
  return (
    <div>
      <h3 style={sectionTitle}>Goals</h3>
      <p style={{ fontSize: '13px', color: '#666', marginTop: '-8px', marginBottom: '16px' }}>
        A goal is what a supplier delivers against. Leads can't be uploaded to a program
        until it has at least one.
      </p>
      {components.map((c, ci) => (
        <div key={ci} style={card}>
          <strong style={{ fontSize: '13px', color: '#2c3e50' }}>
            {c.component_name || `Component ${ci + 1}`}
          </strong>
          {c.goals.map((g, gi) => (
            <div key={gi} style={{
              borderTop: '1px solid #eee', marginTop: '12px', paddingTop: '12px',
            }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '10px' }}>
                <span style={{ fontSize: '12px', color: '#888' }}>Goal {gi + 1}</span>
                <button type="button" style={dangerLink} onClick={() => removeGoal(ci, gi)}>Remove</button>
              </div>
              <div style={grid2}>
                <FormField label="Goal Name">
                  <input style={FORM_INPUT_STYLE} value={g.goal_name}
                    onChange={e => patchGoal(ci, gi, { goal_name: e.target.value })} />
                </FormField>
                <SearchableSelect
                  label="Supplier"
                  options={supplierOpts}
                  value={g.supplier_id}
                  onChange={(v) => patchGoal(ci, gi, { supplier_id: v })}
                  placeholder="Search suppliers…"
                />
              </div>
              <div style={{ ...grid3, marginTop: '12px' }}>
                <FormField label="Quantity Ordered">
                  <input type="number" style={FORM_INPUT_STYLE} value={g.quantity_ordered}
                    onChange={e => patchGoal(ci, gi, { quantity_ordered: e.target.value })} />
                </FormField>
                <FormField label="Spend Per">
                  <input type="number" step="0.01" style={FORM_INPUT_STYLE} value={g.spend_per}
                    onChange={e => patchGoal(ci, gi, { spend_per: e.target.value })} />
                </FormField>
                <div />
                <FormField label="Flight Start">
                  <input type="date" style={FORM_INPUT_STYLE} value={g.flight_date_start}
                    onChange={e => patchGoal(ci, gi, { flight_date_start: e.target.value })} />
                </FormField>
                <FormField label="Flight End"
                  error={dateRangeError(g.flight_date_start, g.flight_date_end)}>
                  <input type="date" style={FORM_INPUT_STYLE} value={g.flight_date_end}
                    onChange={e => patchGoal(ci, gi, { flight_date_end: e.target.value })} />
                </FormField>
              </div>
            </div>
          ))}
          <button type="button" style={{ ...smallButton, marginTop: '12px' }}
            onClick={() => addGoal(ci)}>
            + Add Goal
          </button>
        </div>
      ))}
    </div>
  );
}

export function ReviewStep({ program, components, pdf, clients, onOpenPdf }) {
  const nameOf = (list, id) => list.find(x => String(x.id) === String(id))?.name || '—';
  const goalCount = components.reduce((n, c) => n + c.goals.length, 0);
  const row = (label, value) => (
    <div style={{ display: 'flex', gap: '12px', padding: '5px 0', fontSize: '13px' }}>
      <span style={{ color: '#888', minWidth: '150px' }}>{label}</span>
      <span style={{ color: '#2c3e50' }}>{value || '—'}</span>
    </div>
  );
  return (
    <div>
      <h3 style={sectionTitle}>Review</h3>
      <div style={card}>
        {row('Program', program.program_name)}
        {row('Client', nameOf(clients, program.client_id))}
        {row('Agency', program.agency_id ? nameOf(clients, program.agency_id) : '—')}
        {row('Order date', program.order_date)}
        {row('Flight', `${program.flight_date_start || '—'} → ${program.flight_date_end || '—'}`)}
        {row('Insertion order', !pdf.key ? 'none on file'
          : onOpenPdf ? <OpenPdfLink filename={pdf.filename} onOpen={onOpenPdf} />
          : (pdf.filename || 'attached'))}
      </div>
      <div style={card}>
        {row('Components', components.length
          ? components.map(c => c.component_name || 'Untitled').join(', ')
          : 'none')}
        {row('Goals', goalCount ? String(goalCount) : 'none')}
        {components.some(c => !c.product_id && c.product_name) && (
          <div style={{ fontSize: '12px', color: '#7d6608', marginTop: '8px' }}>
            Some components name a product that wasn't matched to an existing one. A new
            product will be created for each unmatched name.
            {' '}({components.filter(c => !c.product_id && c.product_name)
              .map(c => c.product_name).join(', ')})
          </div>
        )}
      </div>
      {goalCount === 0 && (
        <div style={{
          background: '#fef9e7', border: '1px solid #f7dc6f', borderRadius: '6px',
          padding: '12px 14px', fontSize: '13px', color: '#7d6608',
        }}>
          This program has no goals yet, so suppliers can't upload leads against it and no
          NeTRA Lead IDs can be generated. Add them from the Goals page when you're ready.
        </div>
      )}
      {!pdf.key && (
        <div style={{
          background: '#fef9e7', border: '1px solid #f7dc6f', borderRadius: '6px',
          padding: '12px 14px', fontSize: '13px', color: '#7d6608', marginTop: '12px',
        }}>
          No insertion order is attached. The program will show as having none on file.
        </div>
      )}
      <p style={{ fontSize: '12px', color: '#888', marginTop: '16px' }}>
        Nothing has been saved yet — everything above is created in one step when you
        press Create Program.
      </p>
    </div>
  );
}

function QuickAddClientModal({ isAgency, form, setForm, saving, error, onSave, onClose }) {
  const canSave = !saving && form.name.trim();
  return (
    <div style={{
      position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.35)',
      display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000,
    }}>
      <div style={{
        background: '#fff', borderRadius: '8px', padding: '22px', width: '440px', maxWidth: '94vw',
      }}>
        <h3 style={{ margin: '0 0 14px', fontSize: '16px', color: '#2c3e50' }}>
          New {isAgency ? 'Agency' : 'Client'}
        </h3>
        <ClientFormFields
          form={form}
          onChange={(field, value) => setForm(f => ({ ...f, [field]: value }))}
          autoFocusName
          onEnter={onSave}
          columns="1fr 1fr 80px"
        />
        <p style={{ fontSize: '12px', color: '#888', marginTop: '8px' }}>
          Only the name is required. Contacts and everything else are editable on the Clients page.
        </p>
        {error && <div style={{ color: '#c0392b', fontSize: '12px', marginTop: '8px' }}>{error}</div>}
        <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '10px', marginTop: '16px' }}>
          <button type="button" style={smallButton} onClick={onClose}>Cancel</button>
          <button type="button" disabled={!canSave}
            style={{
              ...smallButton, background: '#2c3e50', color: '#fff', borderColor: '#2c3e50',
              opacity: canSave ? 1 : 0.6,
            }}
            onClick={onSave}>
            {saving ? 'Creating…' : 'Create'}
          </button>
        </div>
      </div>
    </div>
  );
}
